/**
 * DataSource backed by Supabase (RLS-scoped reads/writes and RPCs as the signed-in user) and the
 * Elio Travels Worker API for operations that need server-only credentials (uploads, deletion
 * lifecycle, email). The browser only ever holds the public publishable key.
 */
import type { DocumentReading } from '../domain/wallet';
import { createClient, type PostgrestError, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../lib/database.types';
import type { EventWithPeople } from '../domain/model';
import { DataError, type AppConfig, type DataSource, type EventInput, type InvitationPreview, type TripBundle } from './source';
import type { ApiErrorBody } from '../domain/errors';
import type { ChatAction, ChatMessage, ChatResponse } from '../domain/assistant';

type Db = SupabaseClient<Database>;

export function toDataError(e: PostgrestError | { message?: string; code?: string } | null | undefined): DataError {
  const msg = e?.message ?? '';
  if (e?.code === '42501' || msg.startsWith('FORBIDDEN')) return new DataError('FORBIDDEN', "You don't have permission to do that.");
  if (msg.startsWith('LAST_OWNER')) return new DataError('VALIDATION_ERROR', 'Transfer ownership to another member first — a group always needs an owner.');
  if (msg.startsWith('INVITATION_WRONG_ACCOUNT')) return new DataError('FORBIDDEN', 'This invitation was sent to a different email address. Sign in with that account to accept it.');
  if (msg.startsWith('INVITATION_INVALID')) return new DataError('VALIDATION_ERROR', 'This invitation is no longer valid. Ask the organiser for a new link.');
  if (e?.code === 'P0002' || msg.startsWith('NOT_FOUND')) return new DataError('NOT_FOUND', 'Not found — it may have been removed.');
  if (e?.code === '23514') return new DataError('VALIDATION_ERROR', 'Some details are inconsistent (for example an end before a start). Please check and try again.');
  if (e?.code === '22023' || msg.startsWith('VALIDATION_ERROR')) return new DataError('VALIDATION_ERROR', msg.replace(/^VALIDATION_ERROR:\s*/, ''));
  if (e?.code === 'PGRST301' || e?.code === '401') return new DataError('UNAUTHORIZED', 'Your session has expired. Please sign in again.');
  return new DataError('PROCESSING_FAILED', 'That did not save. Check your connection and try again.');
}

/** For queries that must return data (lists, single rows, RPC results). */
function must<R extends { data: unknown; error: PostgrestError | null }>(r: R): NonNullable<R['data']> {
  if (r.error) throw toDataError(r.error);
  return r.data as NonNullable<R['data']>;
}
/** For `.maybeSingle()` lookups where "no row" is a legitimate answer. */
function maybe<R extends { data: unknown; error: PostgrestError | null }>(r: R): NonNullable<R['data']> | null {
  if (r.error) throw toDataError(r.error);
  return (r.data ?? null) as NonNullable<R['data']> | null;
}

const CLIENT_CAPABILITIES: AppConfig['capabilities'] = [
  ['assistant', 'Assistant setup required. Manual planning, documents and checks still work.'],
  ['ocr', 'Document reading is not connected yet. Add booking details manually.'],
  ['scanner', 'Malware scanning is not connected.'],
  ['maps', 'Places search is not connected. You can open a search in your maps app.'],
  ['routing', 'Route estimates are not connected, so leave-by times are not calculated.'],
  ['transport_status', 'Live transport status is not connected. Check the operator or the station board.'],
  ['weather', 'Weather is not connected.'],
  ['requirements', 'Live requirements data is not connected.'],
  ['email', 'Email sending is not configured. Share invitation links directly.'],
].map(([capability, userMessage]) => ({ capability: capability!, configured: false, provider: null, coverage: 'none', freshness: 'n/a', userMessage: userMessage! }));

export function createSupabaseSource(url: string, publishableKey: string): DataSource {
  const db: Db = createClient<Database>(url, publishableKey, { auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, flowType: 'pkce' } });

  async function uid(): Promise<string> {
    const { data } = await db.auth.getSession();
    if (!data.session) throw new DataError('UNAUTHORIZED', 'Please sign in.');
    return data.session.user.id;
  }

  async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
    const { data } = await db.auth.getSession();
    let res: Response;
    try {
      res = await fetch(`/api/v1${path}`, {
        ...init,
        headers: { 'Content-Type': 'application/json', ...(data.session ? { Authorization: `Bearer ${data.session.access_token}` } : {}), ...init.headers },
      });
    } catch {
      throw new DataError('PROVIDER_UNAVAILABLE', 'The Elio Travels service is unreachable. Check your connection and try again.');
    }
    const ct = res.headers.get('content-type') ?? '';
    if (!ct.includes('application/json')) throw new DataError('NOT_CONFIGURED', 'The Elio Travels API is not running in this environment.');
    const body = (await res.json()) as T | ApiErrorBody;
    if (!res.ok) {
      const err = (body as ApiErrorBody).error;
      throw new DataError(err?.code ?? 'PROCESSING_FAILED', err?.message ?? 'Request failed', err?.setup ? `setup:${err.setup}` : err?.detail);
    }
    return body as T;
  }

  async function profilesFor(ids: string[]) {
    if (ids.length === 0) return new Map();
    const rows = must(await db.from('profiles').select('id, display_name, avatar_color').in('id', [...new Set(ids)]));
    return new Map(rows.map((p) => [p.id, p]));
  }

  const source: DataSource = {
    kind: 'supabase',

    async getSession() {
      const { data } = await db.auth.getSession();
      return data.session ? { userId: data.session.user.id, email: data.session.user.email ?? null } : null;
    },
    onAuthChange(cb) {
      const { data } = db.auth.onAuthStateChange((_e, s) => cb(s ? { userId: s.user.id, email: s.user.email ?? null } : null));
      return () => data.subscription.unsubscribe();
    },
    async signInWithEmail(email, redirectTo) {
      const { error } = await db.auth.signInWithOtp({ email, options: { emailRedirectTo: redirectTo, shouldCreateUser: true } });
      if (error) throw new DataError(error.status === 429 ? 'RATE_LIMITED' : 'PROCESSING_FAILED', error.status === 429 ? 'Too many sign-in emails. Wait a minute and try again.' : 'We could not send the sign-in link. Check the address and try again.');
    },
    async verifyEmailCode(email, code) {
      const { error } = await db.auth.verifyOtp({ email, token: code, type: 'email' });
      if (error) throw new DataError(error.status === 429 ? 'RATE_LIMITED' : 'VALIDATION_ERROR', error.status === 429 ? 'Too many attempts. Wait a minute and try again.' : 'That code is wrong or has expired. Check the latest email, or send a new one.');
    },
    async verifyEmailLink(tokenHash, type) {
      const { error } = await db.auth.verifyOtp({ token_hash: tokenHash, type });
      if (error) throw new DataError('VALIDATION_ERROR', 'This sign-in link has expired or was already used. Send yourself a new one.');
    },
    async signOut() {
      await db.auth.signOut();
    },
    async getConfig() {
      try {
        const cfg = await api<Omit<AppConfig, 'apiReachable'>>('/config');
        return { ...cfg, apiReachable: true };
      } catch {
        return { environment: 'unknown', limits: { maxUploadBytes: 15 * 1024 * 1024, maxPdfPages: 50 }, scanMode: 'unknown', capabilities: CLIENT_CAPABILITIES, apiReachable: false };
      }
    },

    async getMe() {
      const id = await uid();
      const [p, s] = await Promise.all([db.from('profiles').select('*').eq('id', id).single(), db.from('user_settings').select('*').eq('user_id', id).single()]);
      return { profile: must(p), settings: must(s) };
    },
    async updateProfile(patch) {
      must(await db.from('profiles').update(patch).eq('id', await uid()));
    },
    async updateSettings(patch) {
      must(await db.from('user_settings').update(patch).eq('user_id', await uid()));
    },

    async listTrips() {
      const me = await uid();
      const [trips, mem] = await Promise.all([
        db.from('trips').select('*').order('start_date', { ascending: true, nullsFirst: false }),
        db.from('trip_members').select('trip_id, status').eq('user_id', me),
      ]);
      const status = new Map(must(mem).map((m) => [m.trip_id, m.status]));
      return must(trips).map((t) => ({ ...t, myStatus: status.get(t.id) ?? null }));
    },
    async getTrip(id) {
      const me = await uid();
      const trip = maybe(await db.from('trips').select('*').eq('id', id).maybeSingle());
      if (!trip) throw new DataError('NOT_FOUND', 'This trip does not exist or you no longer have access.');
      const [stops, members, subgroups, events] = await Promise.all([
        db.from('trip_stops').select('*').eq('trip_id', id).order('position'),
        db.from('trip_members').select('*').eq('trip_id', id),
        db.from('subgroups').select('*, subgroup_members(user_id)').eq('trip_id', id),
        db.from('itinerary_events').select('*, event_participants(user_id)').eq('trip_id', id).order('start_utc', { nullsFirst: false }).order('sort_key'),
      ]);
      const memberRows = must(members);
      const profiles = await profilesFor(memberRows.map((m) => m.user_id));
      let group: TripBundle['group'] = null;
      if (trip.group_id) {
        const g = await db.from('groups').select('id, name').eq('id', trip.group_id).maybeSingle();
        group = g.data ?? null;
      }
      const mine = memberRows.find((m) => m.user_id === me);
      return {
        trip,
        stops: must(stops),
        members: memberRows.filter((m) => m.status !== 'removed').map((m) => ({ ...m, profile: profiles.get(m.user_id) ?? null })),
        subgroups: must(subgroups).map(({ subgroup_members, ...s }) => ({ ...s, member_ids: (subgroup_members as { user_id: string }[]).map((x) => x.user_id) })),
        events: must(events).map(({ event_participants, ...e }) => ({ ...e, participant_ids: (event_participants as { user_id: string }[]).map((x) => x.user_id) }) as EventWithPeople),
        myRole: trip.owner_id === me ? 'organizer' : mine?.status === 'accepted' ? mine.role : null,
        myStatus: trip.owner_id === me ? 'accepted' : mine?.status ?? null,
        group,
      };
    },
    async createTrip(input) {
      return must(await db.rpc('create_trip', {
        p_title: input.title, p_date_mode: input.date_mode, p_start: input.start_date as string, p_end: input.end_date as string,
        p_date_note: input.date_note as string, p_origin_city: input.origin_city as string, p_origin_tz: input.origin_timezone as string,
        p_currency: input.reporting_currency, p_has_bookings: input.has_existing_bookings, p_group: input.group_id as string,
        p_stops: input.stops as never,
      }));
    },
    async updateTrip(id, version, patch) {
      const rows = must(await db.from('trips').update(patch).eq('id', id).eq('version', version).select('id'));
      if (rows.length === 0) throw new DataError('VERSION_CONFLICT', 'This trip changed since you opened it. Your view has been refreshed — please re-apply your edit.');
    },
    async saveStops(tripId, stops) {
      const existing = must(await db.from('trip_stops').select('id, position').eq('trip_id', tripId));
      const keep = new Set(stops.map((s) => s.id).filter(Boolean));
      const removed = existing.filter((e) => !keep.has(e.id)).map((e) => e.id);
      if (removed.length) must(await db.from('trip_stops').delete().in('id', removed));
      // Move kept rows out of the way first so position uniqueness never collides mid-update.
      for (const e of existing.filter((x) => keep.has(x.id))) must(await db.from('trip_stops').update({ position: 1000 + e.position }).eq('id', e.id));
      for (const [i, s] of stops.entries()) {
        const row = { city: s.city.trim(), country_code: s.country_code || null, timezone: s.timezone || null, arrive_on: s.arrive_on || null, depart_on: s.depart_on || null, position: i };
        if (s.id) must(await db.from('trip_stops').update(row).eq('id', s.id));
        else must(await db.from('trip_stops').insert({ ...row, trip_id: tripId }));
      }
    },
    async deleteTrip(id) {
      must(await db.from('trips').delete().eq('id', id));
    },
    async respondTripInvitation(tripId, accept) {
      must(await db.rpc('respond_trip_invitation', { p_trip: tripId, p_accept: accept }));
    },

    async createEvent(input, participantIds = []) {
      const row = must(await db.from('itinerary_events').insert(input as never).select('id').single());
      if (participantIds.length) must(await db.from('event_participants').insert(participantIds.map((u) => ({ event_id: row.id, user_id: u }))));
    },
    async updateEvent(id, version, patch, participantIds) {
      const rows = must(await db.from('itinerary_events').update(patch as never).eq('id', id).eq('version', version).select('id'));
      if (rows.length === 0) throw new DataError('VERSION_CONFLICT', 'Someone else changed this item since you opened it. Your view has been refreshed; please re-apply your change.');
      if (participantIds) {
        const current = must(await db.from('event_participants').select('user_id').eq('event_id', id)).map((r) => r.user_id);
        const remove = current.filter((u) => !participantIds.includes(u));
        const add = participantIds.filter((u) => !current.includes(u));
        if (remove.length) must(await db.from('event_participants').delete().eq('event_id', id).in('user_id', remove));
        if (add.length) must(await db.from('event_participants').insert(add.map((u) => ({ event_id: id, user_id: u }))));
      }
    },
    async deleteEvent(id) {
      must(await db.from('itinerary_events').delete().eq('id', id));
    },
    async reorderEvents(tripId, ids) {
      must(await db.rpc('reorder_events', { p_trip: tripId, p_ids: ids }));
    },
    async createManualBooking(tripId, event, booking) {
      must(await db.rpc('create_manual_booking', { p_trip: tripId, p_event: event as never, p_booking: booking as never }));
    },
    async listMyBookings(tripId) {
      return must(await db.from('bookings').select('*').eq('trip_id', tripId).eq('owner_id', await uid()).order('created_at'));
    },
    async updateBooking(id, version, patch) {
      const { provider_name, reference, price_minor, currency, fare_restrictions, cancellation_terms, notes, baggage, traveler_count, status } = patch;
      const clean = Object.fromEntries(Object.entries({ provider_name, reference, price_minor, currency, fare_restrictions, cancellation_terms, notes, baggage, traveler_count, status }).filter(([, v]) => v !== undefined));
      const rows = must(await db.from('bookings').update(clean as never).eq('id', id).eq('version', version).select('id'));
      if (rows.length === 0) throw new DataError('VERSION_CONFLICT', 'This booking changed since you opened it. Your view has been refreshed; please re-apply your change.');
    },
    async deleteBooking(booking) {
      must(await db.from('bookings').delete().eq('id', booking.id));
      if (booking.event_id) must(await db.from('itinerary_events').delete().eq('id', booking.event_id));
    },

    async listAttentionActions(tripId) {
      return must(await db.from('attention_actions').select('*').eq('trip_id', tripId).order('created_at'));
    },
    async recordAttention(tripId, fingerprint, action, reason = null, snoozedUntil = null) {
      must(await db.from('attention_actions').insert({ trip_id: tripId, fingerprint, action, reason, snoozed_until: snoozedUntil }));
    },

    async listChecklist(tripId) {
      return must(await db.from('checklist_items').select('*').eq('trip_id', tripId).order('completed_at', { nullsFirst: true }).order('created_at'));
    },
    async addChecklist(tripId, title, opts) {
      must(await db.from('checklist_items').insert({ trip_id: tripId, title, visibility: opts.visibility, due_on: opts.due_on ?? null, assignee_id: opts.assignee_id ?? null }));
    },
    async setChecklistDone(item, done) {
      const me = await uid();
      must(await db.from('checklist_items').update({ completed_at: done ? new Date().toISOString() : null, completed_by: done ? me : null }).eq('id', item.id));
    },
    async deleteChecklist(id) {
      must(await db.from('checklist_items').delete().eq('id', id));
    },

    async listGroups() {
      const me = await uid();
      const mine = must(await db.from('group_members').select('group_id, role, status').eq('user_id', me).in('status', ['active', 'pending_approval']));
      if (!mine.length) return [];
      const ids = mine.map((m) => m.group_id);
      const [groups, members] = await Promise.all([db.from('groups').select('*').in('id', ids), db.from('group_members').select('group_id').in('group_id', ids).eq('status', 'active')]);
      const counts = new Map<string, number>();
      for (const m of must(members)) counts.set(m.group_id, (counts.get(m.group_id) ?? 0) + 1);
      const byId = new Map(mine.map((m) => [m.group_id, m]));
      return must(groups).map((g) => ({ ...g, myRole: byId.get(g.id)!.role, myStatus: byId.get(g.id)!.status, memberCount: counts.get(g.id) ?? 0 }));
    },
    async getGroup(id) {
      const me = await uid();
      const group = maybe(await db.from('groups').select('*').eq('id', id).maybeSingle());
      if (!group) throw new DataError('NOT_FOUND', 'This group does not exist or you are not a member.');
      const [members, invitations, trips] = await Promise.all([
        db.from('group_members').select('*').eq('group_id', id).in('status', ['active', 'pending_approval']),
        db.from('group_invitations').select('*').eq('group_id', id).order('created_at', { ascending: false }),
        db.from('trips').select('*').eq('group_id', id).order('start_date', { nullsFirst: false }),
      ]);
      const rows = must(members);
      const profiles = await profilesFor(rows.map((m) => m.user_id));
      const myRole = rows.find((m) => m.user_id === me && m.status === 'active')?.role;
      if (!myRole) throw new DataError('FORBIDDEN', 'Your membership is waiting for approval.');
      return { group, members: rows.map((m) => ({ ...m, profile: profiles.get(m.user_id) ?? null })), invitations: must(invitations), trips: must(trips), myRole };
    },
    async createGroup(name, description) {
      return must(await db.rpc('create_group', { p_name: name, p_description: description as string }));
    },
    async createInvitation(groupId, input) {
      try {
        return await api<{ link: string; email: 'sent' | 'not_requested' | 'not_configured' | 'failed' }>(`/groups/${groupId}/invitations`, { method: 'POST', body: JSON.stringify(input) });
      } catch (e) {
        if (!(e instanceof DataError) || (e.code !== 'NOT_CONFIGURED' && e.code !== 'PROVIDER_UNAVAILABLE')) throw e;
        // API not running (local frontend-only dev): create the link directly; no email is sent.
        const data = must(await db.rpc('create_group_invitation', { p_group: groupId, p_email: input.email as string, p_role: input.role, p_expires_hours: input.expiresHours, p_max_uses: input.maxUses })) as { token: string };
        return { link: `${window.location.origin}/join/${data.token}`, email: input.email ? 'not_configured' : 'not_requested' };
      }
    },
    async revokeInvitation(id) {
      must(await db.rpc('revoke_group_invitation', { p_invitation: id }));
    },
    async previewInvitation(token) {
      return must(await db.rpc('preview_group_invitation', { p_token: token })) as unknown as InvitationPreview;
    },
    async acceptInvitation(token) {
      return must(await db.rpc('accept_group_invitation', { p_token: token })) as unknown as { group_id: string; status: string };
    },
    async leaveGroup(groupId) {
      must(await db.rpc('leave_group', { p_group: groupId }));
    },
    async removeGroupMember(groupId, userId) {
      must(await db.rpc('remove_group_member', { p_group: groupId, p_user: userId }));
    },
    async setGroupRole(groupId, userId, role) {
      must(await db.rpc('set_group_member_role', { p_group: groupId, p_user: userId, p_role: role }));
    },
    async approveMember(groupId, userId, approve) {
      must(await db.rpc('approve_group_member', { p_group: groupId, p_user: userId, p_approve: approve }));
    },
    async linkTripToGroup(tripId, version, groupId) {
      await source.updateTrip(tripId, version, { group_id: groupId });
    },
    async inviteToTrip(tripId, userIds, role = 'participant') {
      must(await db.rpc('invite_trip_participants', { p_trip: tripId, p_users: userIds, p_role: role }));
    },
    async setTripRole(tripId, userId, role) {
      must(await db.rpc('set_trip_member_role', { p_trip: tripId, p_user: userId, p_role: role }));
    },
    async removeFromTrip(tripId, userId) {
      must(await db.rpc('remove_trip_member', { p_trip: tripId, p_user: userId }));
    },
    async createSubgroup(tripId, name, memberIds) {
      const sg = must(await db.from('subgroups').insert({ trip_id: tripId, name }).select('id').single());
      if (memberIds.length) must(await db.from('subgroup_members').insert(memberIds.map((u) => ({ subgroup_id: sg.id, user_id: u }))));
    },

    async listDocuments() {
      return must(await db.from('documents').select('*').neq('state', 'deleted').order('created_at', { ascending: false }));
    },
    async uploadDocument(file, meta, onProgress) {
      onProgress?.('reserving');
      const key = `${file.name}:${file.size}:${file.lastModified}:${meta.tripId ?? ''}`;
      const idempotencyKey = `up_${await sha1(key)}`;
      const intent = await api<{ documentId: string; upload: { bucket: 'quarantine'; path: string; token: string } | null }>('/documents/upload-intent', {
        method: 'POST',
        body: JSON.stringify({ fileName: file.name, title: meta.title, mime: file.type, size: file.size, category: meta.category, sensitivity: meta.sensitivity, tripId: meta.tripId, idempotencyKey, identityNoticeAccepted: meta.identityNoticeAccepted }),
      });
      if (intent.upload) {
        onProgress?.('uploading');
        const { error } = await db.storage.from('quarantine').uploadToSignedUrl(intent.upload.path, intent.upload.token, file, { contentType: file.type });
        if (error) throw new DataError('PROCESSING_FAILED', 'The upload was interrupted. Try again — nothing was saved twice.');
      }
      onProgress?.('checking');
      await api(`/documents/${intent.documentId}/finalize`, { method: 'POST', body: '{}' });
      return intent.documentId;
    },
    async getDocumentUrl(doc, download = false) {
      const { data, error } = await db.storage.from('documents').createSignedUrl(doc.storage_path, 60, download ? { download: doc.original_filename } : undefined);
      if (error || !data) throw new DataError('FORBIDDEN', 'This file is not available to you right now.');
      return data.signedUrl;
    },
    async renameDocument(id, title) {
      must(await db.from('documents').update({ title }).eq('id', id));
    },
    async setDocumentTrip(id, tripId) {
      must(await db.from('documents').update({ trip_id: tripId }).eq('id', id));
    },
    async setDocumentCategory(id, category) {
      must(await db.from('documents').update({ category }).eq('id', id).eq('sensitivity', 'standard'));
    },
    async readDocument(req) {
      return api<DocumentReading>('/documents/read', { method: 'POST', body: JSON.stringify(req) });
    },
    async saveDocumentDetails(id, d) {
      must(await db.from('documents').update({
        title: d.title, trip_id: d.trip_id, subtitle: d.subtitle, provider: d.provider, reference: d.reference,
        starts_on: d.starts_on, ends_on: d.ends_on, expires_on: d.expires_on, details_confirmed_at: new Date().toISOString(),
      }).eq('id', id));
      if (d.category !== 'identity') must(await db.from('documents').update({ category: d.category }).eq('id', id).eq('sensitivity', 'standard'));
    },
    async deleteDocument(id) {
      await api(`/documents/${id}`, { method: 'DELETE', body: JSON.stringify({ retainConfirmedFacts: true }) });
    },

    async getBudget(tripId) {
      const me = await uid();
      const [budget, items, expenses, settlements, limit] = await Promise.all([
        db.from('trip_budgets').select('*').eq('trip_id', tripId).maybeSingle(),
        db.from('budget_items').select('*, budget_item_allocations(*)').eq('trip_id', tripId).order('created_at'),
        db.from('expenses').select('*').eq('trip_id', tripId).order('occurred_on', { ascending: false }),
        db.from('settlements').select('*').eq('trip_id', tripId).order('created_at', { ascending: false }),
        db.from('personal_budget_limits').select('limit_minor, currency').eq('trip_id', tripId).eq('user_id', me).maybeSingle(),
      ]);
      const exp = must(expenses);
      const shares = exp.length ? must(await db.from('expense_shares').select('*').in('expense_id', exp.map((e) => e.id))) : [];
      return {
        budget: maybe(budget),
        items: must(items).map(({ budget_item_allocations, ...i }) => ({ ...i, allocations: (budget_item_allocations as never[]).filter((a: { active: boolean }) => a.active) })),
        expenses: exp, shares, settlements: must(settlements), myLimit: maybe(limit),
      };
    },
    async saveBudget(tripId, patch) {
      const existing = maybe(await db.from('trip_budgets').select('version').eq('trip_id', tripId).maybeSingle());
      const { version: _v, trip_id: _t, ...rest } = patch;
      if (existing) must(await db.from('trip_budgets').update(rest).eq('trip_id', tripId));
      else must(await db.from('trip_budgets').insert({ ...rest, trip_id: tripId }));
    },
    async saveBudgetItem(tripId, item) {
      const { id, expected_total_minor: _a, low_total_minor: _b, high_total_minor: _c, version: _d, created_at: _e, updated_at: _f, created_by: _g, ...row } = item as typeof item & Record<string, unknown>;
      if (id) {
        must(await db.from('budget_items').update(row as never).eq('id', id));
        return id;
      }
      return must(await db.from('budget_items').insert({ ...(row as object), trip_id: tripId } as never).select('id').single()).id;
    },
    async deleteBudgetItem(id) {
      must(await db.from('budget_items').delete().eq('id', id));
    },
    async setAllocations(itemId, basis, rows) {
      must(await db.rpc('set_budget_item_allocations', { p_item: itemId, p_basis: basis, p_rows: rows as never }));
    },
    async setMyLimit(tripId, limit) {
      const me = await uid();
      if (!limit) must(await db.from('personal_budget_limits').delete().eq('trip_id', tripId).eq('user_id', me));
      else must(await db.from('personal_budget_limits').upsert({ trip_id: tripId, user_id: me, ...limit }));
    },
    async addExpense(input) {
      return must(await db.rpc('create_expense', {
        p_trip: input.tripId, p_amount_minor: input.amountMinor, p_currency: input.currency, p_occurred_on: input.occurredOn,
        p_idempotency_key: input.idempotencyKey, p_payer: (input.payerId ?? null) as string, p_description: (input.description ?? null) as string,
        p_category: (input.category ?? null) as never, p_visibility: input.visibility, p_shares: (input.shares ?? null) as never,
      }));
    },
    async voidExpense(id, reason) {
      must(await db.rpc('void_expense', { p_expense: id, p_reason: reason }));
    },
    async recordSettlement(tripId, toUser, amountMinor, currency, note) {
      must(await db.rpc('record_settlement', { p_trip: tripId, p_to: toUser, p_amount_minor: amountMinor, p_currency: currency, p_note: (note ?? null) as string }));
    },
    async respondSettlement(id, confirm) {
      must(await db.rpc('respond_settlement', { p_settlement: id, p_confirm: confirm }));
    },

    async getTravelProfile() {
      const me = await uid();
      const [p, c] = await Promise.all([
        db.from('traveler_document_profiles').select('*').eq('user_id', me).maybeSingle(),
        db.from('travel_credentials').select('*').eq('user_id', me).order('created_at'),
      ]);
      return { profile: maybe(p), credentials: must(c) };
    },
    async saveTravelProfile(patch) {
      must(await db.from('traveler_document_profiles').upsert({ user_id: await uid(), ...patch }));
    },
    async saveCredential(c) {
      const { id, version: _v, created_at: _c, updated_at: _u, ...row } = c;
      if (id) must(await db.from('travel_credentials').update(row).eq('id', id));
      else must(await db.from('travel_credentials').insert(row));
    },
    async deleteCredential(id) {
      must(await db.from('travel_credentials').delete().eq('id', id));
    },
    async listDestinations() {
      return must(await db.from('destination_catalog').select('*').order('name'));
    },
    async listSavedDestinations() {
      return must(await db.from('saved_destinations').select('code')).map((r) => r.code);
    },
    async toggleSavedDestination(code, save) {
      if (save) must(await db.from('saved_destinations').upsert({ code, user_id: await uid() }));
      else must(await db.from('saved_destinations').delete().eq('code', code));
    },

    async listConversations() {
      return must(await db.from('conversations').select('id, trip_id, title, last_message_at, created_at').order('last_message_at', { ascending: false }).limit(100));
    },
    async getConversation(id) {
      const conversation = maybe(await db.from('conversations').select('id, trip_id, title, last_message_at, created_at').eq('id', id).maybeSingle());
      if (!conversation) throw new DataError('NOT_FOUND', 'This conversation is no longer available.');
      const [messages, actions] = await Promise.all([
        db.from('conversation_messages').select('id, role, text, created_at, attachments, citations, action_ids, blocks').eq('conversation_id', id).order('created_at').limit(500),
        db.from('assistant_actions').select('id, kind, summary, payload, status, trip_id, result, error').eq('conversation_id', id).order('created_at'),
      ]);
      return { conversation, messages: must(messages) as unknown as ChatMessage[], actions: must(actions) as unknown as ChatAction[] };
    },
    async sendChat(req, onStatus) {
      const { data } = await db.auth.getSession();
      let res: Response;
      try {
        res = await fetch('/api/v1/assistant/chat', {
          method: 'POST', body: JSON.stringify(req),
          headers: { 'Content-Type': 'application/json', Accept: 'application/x-ndjson', ...(data.session ? { Authorization: `Bearer ${data.session.access_token}` } : {}) },
        });
      } catch {
        throw new DataError('PROVIDER_UNAVAILABLE', 'Elio couldn’t be reached. Check your connection and try again.');
      }
      const ct = res.headers.get('content-type') ?? '';
      if (!res.ok || !ct.includes('ndjson') || !res.body) {
        const body = (await res.json().catch(() => null)) as ApiErrorBody | null;
        const err = body?.error;
        throw new DataError(err?.code ?? 'PROCESSING_FAILED', err?.message ?? 'Elio couldn’t answer. Please try again.', err?.setup ? `setup:${err.setup}` : err?.detail);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (value) buf += dec.decode(value, { stream: true });
        let nl: number;
        while ((nl = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, nl).trim(); buf = buf.slice(nl + 1);
          if (!line) continue;
          const ev = JSON.parse(line) as { type: 'status'; status: string } | { type: 'done'; result: ChatResponse } | { type: 'error'; error: ApiErrorBody['error'] };
          if (ev.type === 'status') onStatus?.(ev.status);
          else if (ev.type === 'done') return ev.result;
          else throw new DataError(ev.error.code, ev.error.message, ev.error.setup ? `setup:${ev.error.setup}` : ev.error.detail);
        }
        if (done) break;
      }
      throw new DataError('PROVIDER_UNAVAILABLE', 'The connection to Elio was interrupted. Please try again.');
    },
    async decideAction(id, status, result = null, error = null) {
      must(await db.from('assistant_actions').update({ status, result: result as never, error }).eq('id', id));
    },
    async setConversationTrip(id, tripId) {
      must(await db.from('conversations').update({ trip_id: tripId }).eq('id', id));
    },
    async deleteConversation(id) {
      must(await db.from('conversations').delete().eq('id', id));
    },
    async listMemory() {
      return must(await db.from('travel_memory').select('id, trip_id, kind, text, status, created_at').eq('status', 'active').order('created_at', { ascending: false }));
    },
    async deleteMemory(id) {
      must(await db.from('travel_memory').delete().eq('id', id));
    },
    async closeAccount() {
      await api<{ closed: boolean }>('/account/close', { method: 'POST', body: JSON.stringify({ confirm: 'CLOSE' }) });
      await db.auth.signOut({ scope: 'local' }).catch(() => undefined);
    },
    async placesSearch(q, near) {
      try {
        return await api(`/places/search?${new URLSearchParams({ q, ...(near ? { near } : {}) })}`);
      } catch {
        const query = near ? `${q} near ${near}` : q;
        return { status: 'not_connected', message: 'Places search is not connected.', deepLink: { label: 'Search in Google Maps', url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` } };
      }
    },
    async exportAccount() {
      // Read through RLS as the user: exactly the data this account may see.
      const tables = ['profiles', 'user_settings', 'trips', 'trip_stops', 'itinerary_events', 'bookings', 'checklist_items', 'documents',
        'trip_budgets', 'budget_items', 'expenses', 'settlements', 'traveler_document_profiles', 'travel_credentials', 'saved_destinations'] as const;
      const out: Record<string, unknown> = { exportedAt: new Date().toISOString(), note: 'Document files are not included; download them individually from Documents.' };
      for (const t of tables) out[t] = must(await db.from(t).select('*').limit(5000));
      return new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
    },
  };
  return source;
}

async function sha1(s: string): Promise<string> {
  const d = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(s));
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 32);
}

export type { EventInput };
